'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Bell, Users, Briefcase, FileText, TrendingUp, Settings, AlertTriangle, LogOut, Clock, User, Building, Mail, Phone, CheckCircle, XCircle, Menu, X, ChevronLeft, ChevronRight, ShoppingCart, DollarSign } from 'lucide-react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';

interface DashboardStats {
  totalCompanies: number;
  totalApplicants: number;
  totalJobs: number;
  totalApplications: number;
  activeSubscriptions: number;
  pendingDemos: number;
  recentActivity: any[];
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationInterval, setNotificationInterval] = useState<NodeJS.Timeout | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Desktop sidebar collapse state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const router = useRouter();
  const pathname = usePathname();

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      if (pathname === '/admin/login') {
        setIsAuthenticated(true);
        return;
      }

      const adminToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('admin_token='))
        ?.split('=')[1];

      if (!adminToken) {
        router.push('/admin/login');
        return;
      }

      setIsAuthenticated(true);
    };

    checkAuth();
  }, [router, pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchDashboardData();
    fetchNotifications();

    const interval = setInterval(fetchNotifications, 30000);
    setNotificationInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuthenticated]);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Handle scroll for mobile header minimization
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 50;

      if (currentScrollY > lastScrollY && currentScrollY > scrollThreshold) {
        setHeaderVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    if (window.innerWidth < 768) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY]);

  const handleLogout = () => {
    document.cookie = 'admin_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'admin_user=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    window.location.href = '/admin/login';
  };

  const fetchDashboardData = async () => {
    try {
      const response = await fetch('/api/admin/dashboard');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch('/api/admin/notifications');
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const navigationItems = [
    { name: 'Dashboard', href: '/admin', icon: TrendingUp },
    { name: 'Companies', href: '/admin/companies', icon: Building },
    { name: 'Applicants', href: '/admin/applicants', icon: Users },
    { name: 'Subscriptions', href: '/admin/subscriptions', icon: FileText },
    { name: 'Plans', href: '/admin/plans', icon: Settings },
    { name: 'OTP Purchases', href: '/admin/otp', icon: ShoppingCart },
    { name: 'Payments', href: '/admin/payments', icon: DollarSign },
    { name: 'Demo Requests', href: '/admin/demo-requests', icon: User },
    { name: 'Jobs', href: '/admin/jobs', icon: Briefcase },
    { name: 'Applications', href: '/admin/applications', icon: FileText },
    { name: 'Analytics', href: '/admin/analytics', icon: TrendingUp },
  ];

  const getPageTitle = () => {
    const currentItem = navigationItems.find(item => item.href === pathname);
    return currentItem ? `${currentItem.name}` : 'Dashboard';
  };

  if (pathname === '/admin/login') {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading admin panel...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: Collapsible, Mobile: Overlay */}
      <aside
        className={`
          fixed top-0 left-0 h-screen bg-white shadow-lg z-50
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${sidebarCollapsed ? 'md:w-20' : 'md:w-64'}
          w-64
        `}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b bg-blue-600 text-white h-[65px]">
          <h2 className={`text-lg font-bold transition-all duration-300 ${sidebarCollapsed ? 'md:opacity-0 md:w-0 md:overflow-hidden' : 'opacity-100'}`}>
            Admin Panel
          </h2>
          
          {/* Desktop Toggle Button - In Header */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:flex text-white hover:bg-blue-700 p-2"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </Button>

          {/* Mobile Close Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-white hover:bg-blue-700 p-2"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="overflow-y-auto h-[calc(100vh-65px)]">
          <div className="py-4">
            {navigationItems.map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  router.push(item.href);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center px-4 py-3 text-left transition-all duration-300
                  hover:bg-blue-50 hover:text-blue-600 group relative
                  ${pathname === item.href 
                    ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600 font-medium' 
                    : 'text-gray-700'
                  }
                  ${sidebarCollapsed ? 'md:justify-center md:px-0' : ''}
                `}
                title={sidebarCollapsed ? item.name : ''}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${sidebarCollapsed ? 'md:mx-auto' : 'mr-3'}`} />
                <span className={`transition-all duration-300 whitespace-nowrap overflow-hidden ${sidebarCollapsed ? 'md:w-0 md:opacity-0' : 'w-auto opacity-100'}`}>
                  {item.name}
                </span>
                
                {/* Tooltip for collapsed state */}
                {sidebarCollapsed && (
                  <div className="hidden md:block absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50">
                    {item.name}
                  </div>
                )}
              </button>
            ))}
          </div>
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
        {/* Header */}
        <header className={`bg-white shadow-sm border-b sticky top-0 z-30 transition-transform duration-300 ease-in-out ${headerVisible ? 'translate-y-0' : '-translate-y-full'} md:translate-y-0`}>
          <div className="flex justify-between items-center px-3 md:px-6 py-3 md:py-4">
            <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
              {/* Mobile Hamburger Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h1 className="text-lg md:text-2xl font-bold truncate">{getPageTitle()}</h1>
            </div>

            <div className="flex gap-1 md:gap-2 flex-shrink-0">
              {/* Notifications Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="relative">
                    <Bell className="w-4 h-4 md:mr-2" />
                    <span className="hidden md:inline">Notifications</span>
                    {notifications.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {notifications.length}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] md:w-80 max-h-[70vh] overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No notifications
                    </div>
                  ) : (
                    <>
                      {notifications.map((notification, index) => (
                        <div key={notification.id || index}>
                          <DropdownMenuItem className="flex flex-col items-start p-3 md:p-4 cursor-pointer hover:bg-muted/50">
                            <div className="flex items-start gap-2 md:gap-3 w-full">
                              <div className="mt-0.5 flex-shrink-0">
                                {notification.type === 'new_registrations' && <Building className="w-4 h-4 text-blue-500" />}
                                {notification.type === 'subscription_expired' && <AlertTriangle className="w-4 h-4 text-orange-500" />}
                                {notification.type === 'demo_requests' && <User className="w-4 h-4 text-green-500" />}
                                {notification.type === 'applicant_demo_requests' && <User className="w-4 h-4 text-purple-500" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm">{notification.title}</div>
                                <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{notification.message}</div>
                                <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(notification.timestamp).toLocaleString()}
                                </div>
                                {(notification.type === 'demo_requests' || notification.type === 'applicant_demo_requests') && notification.data && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {notification.data.slice(0, 2).map((item: any, idx: number) => (
                                      <div key={idx} className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-6 px-2 text-xs"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(`mailto:${item.email}`, '_blank');
                                          }}
                                        >
                                          <Mail className="w-3 h-3 mr-1" />
                                          Email
                                        </Button>
                                        {item.phone_number && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-6 px-2 text-xs"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(`tel:${item.phone_number}`, '_blank');
                                            }}
                                          >
                                            <Phone className="w-3 h-3 mr-1" />
                                            Call
                                          </Button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <Badge variant={notification.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                                  {notification.priority}
                                </Badge>
                                {notification.data && (
                                  <span className="text-xs text-muted-foreground">
                                    {notification.data.length}
                                  </span>
                                )}
                              </div>
                            </div>
                          </DropdownMenuItem>
                          {index < notifications.length - 1 && <DropdownMenuSeparator />}
                        </div>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Settings - Hidden on mobile */}
              <Button variant="outline" size="sm" className="hidden lg:flex">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>

              {/* Logout */}
              <Button variant="destructive" size="sm" onClick={handleLogout} className="flex-shrink-0">
                <LogOut className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Logout</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-3 md:p-6 min-h-[calc(100vh-73px)]">
          {children}
        </main>
      </div>
    </div>
  );
}
